import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SortingState, ColumnFiltersState } from "@tanstack/react-table";
import { toast } from "sonner";
import { Plus, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuthStore } from "@/app/store/authStore";
import { Link } from "react-router-dom";
import { PaginatedResponse, ListQueryParams } from "@/app/types/api";
import { Unit, UnitCreate, UnitUpdate } from "@/app/types/unit";
import { listUnits, createUnit, updateUnit, deleteUnit } from "@/app/services/unitService";
import UnitDataTable from "./components/UnitDataTable";
import UnitForm from "./components/UnitForm";
import { columns as unitColumns } from "./components/columns";
import { AuthState } from "@/app/store/authStore";
import { User } from "@/app/types/auth";

// Define selectors for auth store
const selectUser = (state: AuthState): User | null => state.user;
const selectLogoutAction = (state: AuthState): (() => void) => state.logoutAction;
const selectIsLoading = (state: AuthState): boolean => state.isLoading;

const UnitsPage: React.FC = () => {
  const queryClient = useQueryClient();
  
  // Local State
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentUnit, setCurrentUnit] = useState<Unit | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    pageCount: 0,
  });
  
  // Auth state
  const user = useAuthStore(selectUser);
  const logoutAction = useAuthStore(selectLogoutAction);
  const isAuthLoading = useAuthStore(selectIsLoading);
  
  // Get organization ID from user data
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user]);
  const isOrgAdmin = useMemo(() => user?.role?.name === 'org_admin', [user]);
  
  // Construct query parameters for the API
  const queryParams = useMemo<ListQueryParams>(() => {
    const params: ListQueryParams = {
      skip: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      search_query: searchValue || undefined,
    };
    
    // Add sorting if defined
    if (sorting.length > 0) {
      params.sort_by = sorting[0].id;
      params.sort_dir = sorting[0].desc ? "desc" : "asc";
    }
    
    // Add filters if defined
    if (columnFilters.length > 0) {
      const filters: Record<string, any> = {};
      columnFilters.forEach((filter) => {
        filters[filter.id] = filter.value;
      });
      params.filters = JSON.stringify(filters);
    }
    
    return params;
  }, [pagination.pageIndex, pagination.pageSize, searchValue, sorting, columnFilters]);
  
  // Query to fetch units
  const { 
    data: unitsResponse = { items: [], total: 0 },
    isLoading: isUnitsLoading, 
    isError: isUnitsError,
    error: unitsError,
    refetch: refetchUnits,
  } = useQuery<PaginatedResponse<Unit>>({
    queryKey: ['units', userOrgId, queryParams],
    queryFn: () => {
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      return listUnits(queryParams, userOrgId);
    },
    enabled: !!userOrgId,
  });
  
  // Update pagination when total changes
  useEffect(() => {
    if (unitsResponse) {
      setPagination(prev => ({
        ...prev,
        pageCount: Math.ceil(unitsResponse.total / prev.pageSize),
      }));
    }
  }, [unitsResponse]);

  // Setup event listeners for edit/delete actions from the table
  useEffect(() => {
    const handleEditUnit = (event: CustomEvent<Unit>) => {
      setCurrentUnit(event.detail);
      setIsEditFormOpen(true);
    };

    const handleDeleteUnit = (event: CustomEvent<Unit>) => {
      setCurrentUnit(event.detail);
      setIsDeleteDialogOpen(true);
    };

    document.addEventListener('edit-unit', handleEditUnit as EventListener);
    document.addEventListener('delete-unit', handleDeleteUnit as EventListener);

    return () => {
      document.removeEventListener('edit-unit', handleEditUnit as EventListener);
      document.removeEventListener('delete-unit', handleDeleteUnit as EventListener);
    };
  }, []);
  
  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: UnitCreate) => createUnit(data, userOrgId!),
    onSuccess: () => {
      toast.success('Unit created successfully');
      queryClient.invalidateQueries({ queryKey: ['units', userOrgId] });
      setIsCreateFormOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create unit: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UnitUpdate }) => updateUnit(id, data),
    onSuccess: () => {
      toast.success('Unit updated successfully');
      queryClient.invalidateQueries({ queryKey: ['units', userOrgId] });
      setIsEditFormOpen(false);
      setCurrentUnit(null);
    },
    onError: (error) => {
      toast.error(`Failed to update unit: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteUnit(id),
    onSuccess: () => {
      toast.success('Unit deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['units', userOrgId] });
      setIsDeleteDialogOpen(false);
      setCurrentUnit(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete unit: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });
  
  // Event Handlers
  const handleCreateClick = (): void => {
    setIsCreateFormOpen(true);
  };

  const handleCreateSubmit = (formData: UnitCreate): void => {
    createMutation.mutate(formData);
  };

  const handleEditSubmit = (formData: UnitUpdate): void => {
    if (currentUnit) {
      updateMutation.mutate({ id: currentUnit.id, data: formData });
    }
  };

  const handleDeleteConfirm = (): void => {
    if (currentUnit) {
      deleteMutation.mutate(currentUnit.id);
    }
  };
  
  const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }): void => {
    setPagination(prev => ({
      ...prev,
      pageIndex: newPagination.pageIndex,
      pageSize: newPagination.pageSize,
    }));
  };
  
  // --- Render Logic ---
  
  // Show loading indicator if auth state is loading
  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }
  
  // No organization assigned
  if (!isAuthLoading && !userOrgId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4 text-center p-8">
        <AlertTriangle className="h-16 w-16 text-warning" />
        <h2 className="text-2xl font-semibold">No Organization Assigned</h2>
        <p className="max-w-md text-muted-foreground">
          You need to be assigned to an organization to manage units.
          Please contact your administrator.
        </p>
        <div className="flex gap-4 mt-4">
          <Link to="/dashboard">
            <Button variant="outline">Go to Dashboard</Button>
          </Link>
          <Button onClick={logoutAction} variant="destructive">
            Logout
          </Button>
        </div>
      </div>
    );
  }
  
  // Handle error state
  if (isUnitsError && !!userOrgId) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-xl font-semibold text-destructive">Error Loading Units</p>
        <p className="text-sm text-muted-foreground">
          {unitsError instanceof Error ? unitsError.message : 'An unknown error occurred.'}
        </p>
        <Button onClick={() => refetchUnits()} variant="outline">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Retry
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Units</h1>
        {isOrgAdmin && (
          <Button onClick={handleCreateClick}>
            <Plus className="mr-2 h-4 w-4" /> Add Unit
          </Button>
        )}
      </div>
      <p className="text-muted-foreground">
        Manage measurement units for your business (e.g., KG, NOS, Box).
      </p>
      
      {/* Units Data Table */}
      <UnitDataTable
        data={unitsResponse}
        columns={unitColumns}
        isLoading={isUnitsLoading}
        isOrgAdmin={isOrgAdmin}
        pagination={pagination}
        sorting={sorting}
        columnFilters={columnFilters}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onPaginationChange={handlePaginationChange}
        onSortingChange={setSorting}
        onColumnFiltersChange={setColumnFilters}
      />
      
      {/* Create Form */}
      <UnitForm
        open={isCreateFormOpen}
        onOpenChange={setIsCreateFormOpen}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        mode="create"
      />
      
      {/* Edit Form */}
      {currentUnit && (
        <UnitForm
          open={isEditFormOpen}
          onOpenChange={(open) => {
            setIsEditFormOpen(open);
            if (!open) setCurrentUnit(null);
          }}
          onSubmit={handleEditSubmit}
          defaultValues={currentUnit}
          isLoading={updateMutation.isPending}
          mode="edit"
        />
      )}
      
      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the unit &quot;{currentUnit?.name}&quot;.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UnitsPage; 